import api from './api';
import type { User, CreateUserData, UpdateUserData } from '../types/user';

/** 在職状態での絞り込み。既定は在職者のみ。 */
export type Employment = 'active' | 'retired' | 'all';

// ユーザー一覧取得（standalone export）
export async function getUsers(employment: Employment = 'active'): Promise<User[]> {
  const response = await api.get<{ users: User[] }>('/users', {
    params: { employment },
  });
  return response.data.users;
}

/** 従業員を削除したときに一緒に消えるものの件数。 */
export interface DeletionImpact {
  user: { id: number; username: string; name: string };
  attendance: { count: number; from: string | null; to: string | null };
  leaveRequests: number;
  leaveBalances: number;
  correctionRequests: number;
}

export const userService = {
  // ユーザー一覧取得
  getUsers,

  // ユーザー作成
  async createUser(data: CreateUserData): Promise<User> {
    const response = await api.post<{ message: string; user: User }>('/users', data);
    return response.data.user;
  },

  // ユーザー更新
  async updateUser(id: number, data: UpdateUserData): Promise<User> {
    const response = await api.put<{ message: string; user: User }>(`/users/${id}`, data);
    return response.data.user;
  },

  // 退職 / 復職。**日常の「辞めた人を外す」操作はこちら。**
  // 記録は残り、ログイン不可・請求人数の対象外になる。
  async setRetired(id: number, retired: boolean): Promise<void> {
    await api.patch(`/users/${id}/retirement`, { retired });
  },

  // 削除したときに何が消えるかを数える（削除前の確認に使う）
  async getDeletionImpact(id: number): Promise<DeletionImpact> {
    const response = await api.get<DeletionImpact>(`/users/${id}/deletion-impact`);
    return response.data;
  },

  // ユーザー削除
  //
  // ⚠️ 物理削除で、**その従業員の打刻記録も一緒に消える**。
  // 呼ぶ前に必ず `getDeletionImpact` の件数を提示すること。
  async deleteUser(id: number): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  // パスワードリセット
  async resetPassword(id: number): Promise<{ message: string; temporaryPassword: string; username: string; name: string }> {
    const response = await api.post<{ message: string; temporaryPassword: string; username: string; name: string }>(`/users/${id}/reset-password`);
    return response.data;
  },
};
